import { ProjectIcon } from "./ProjectIcon";
import { useProjects } from "../state/entities";
import { useProjectIcon } from "../projectPresentation";
import { scopeProjectRef } from "@afar-research/client-runtime/environment";
import type { EnvironmentId } from "@afar-research/contracts";
import { FolderIcon } from "lucide-react";
import { useState } from "react";
import { useAssetUrl } from "../assets/assetUrls";

const loadedProjectFaviconSrcs = new Set<string>();

export function ProjectFavicon(input: {
  environmentId: EnvironmentId;
  cwd: string;
  className?: string | undefined;
}) {
  const src = useAssetUrl(input.environmentId, {
    _tag: "project-favicon",
    cwd: input.cwd,
  });

  const projects = useProjects();
  const project = projects.find(
    (project) =>
      project.environmentId === input.environmentId && project.workspaceRoot === input.cwd,
  );
  const icon = useProjectIcon(project ? scopeProjectRef(project.environmentId, project.id) : null);
  if (icon) return <ProjectIcon icon={icon} className={`size-3.5 ${input.className ?? ""}`} />;

  if (!src) {
    return <ProjectFaviconFallback className={input.className} />;
  }

  return <ProjectFaviconImage key={src} src={src} className={input.className} />;
}

function ProjectFaviconFallback({ className }: { readonly className?: string | undefined }) {
  return <FolderIcon className={`size-3.5 shrink-0 text-muted-foreground/50 ${className ?? ""}`} />;
}

function ProjectFaviconImage({
  src,
  className,
}: {
  readonly src: string;
  readonly className?: string | undefined;
}) {
  const [status, setStatus] = useState<"loading" | "loaded" | "error">(() =>
    loadedProjectFaviconSrcs.has(src) ? "loaded" : "loading",
  );

  return (
    <>
      {status !== "loaded" ? <ProjectFaviconFallback className={className} /> : null}
      <img
        src={src}
        alt=""
        className={`size-3.5 shrink-0 rounded-sm object-contain ${status === "loaded" ? "" : "hidden"} ${className ?? ""}`}
        onLoad={() => {
          loadedProjectFaviconSrcs.add(src);
          setStatus("loaded");
        }}
        onError={() => setStatus("error")}
      />
    </>
  );
}
